import { useState } from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowUpRight, ChevronDown, Menu, X } from "lucide-react";
import { SERVICES } from "../data/content";

const NAV_LINKS = [
  { to: "/pricing/", label: "Pricing" },
  { to: "/projects/", label: "Work" },
  { to: "/blog/", label: "Insights" },
  { to: "/reviews/", label: "Reviews" },
  { to: "/about/", label: "About" },
];

export const openLeadPopup = () =>
  window.dispatchEvent(new CustomEvent("open-lead-popup"));

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const goHomeSection = (id) => {
    setMobileOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" }), 350);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <>
      <header className="fixed inset-x-0 top-4 z-50 px-4" data-testid="main-nav">
        <div className="mx-auto flex max-w-6xl items-center justify-between rounded-full border border-white/10 bg-black/60 px-5 py-3 backdrop-blur-xl">
          <Link
            to="/"
            data-testid="nav-logo"
            className="font-display text-sm font-extrabold tracking-[0.18em] text-white"
          >
            GROTECH WEB
          </Link>

          <nav className="hidden items-center gap-7 lg:flex">
            <div
              className="relative"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              <Link
                to="/services/"
                data-testid="nav-services-btn"
                className="flex items-center gap-1 text-sm text-white/70 transition-colors hover:text-white"
              >
                Services <ChevronDown className="h-3.5 w-3.5" />
              </Link>
              <AnimatePresence>
                {servicesOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: 8 }}
                    transition={{ duration: 0.2 }}
                    className="absolute left-1/2 top-full w-80 -translate-x-1/2 pt-4"
                  >
                    <div className="max-h-[70vh] overflow-y-auto rounded-2xl border border-white/10 bg-ink p-2 shadow-2xl">
                      {SERVICES.map((s) => (
                        <Link
                          key={s.slug}
                          to={`/${s.slug}/`}
                          data-testid={`nav-service-${s.slug}`}
                          onClick={() => setServicesOpen(false)}
                          className="block rounded-xl px-4 py-2.5 text-sm text-white/70 transition-colors hover:bg-white/5 hover:text-white"
                        >
                          {s.title}
                        </Link>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
            <button
              data-testid="nav-process-btn"
              onClick={() => goHomeSection("process")}
              className="text-sm text-white/70 transition-colors hover:text-white"
            >
              Process
            </button>
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="text-sm text-white/70 transition-colors hover:text-white"
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-cta-btn"
              onClick={openLeadPopup}
              className="hidden items-center gap-1.5 rounded-full bg-ember px-5 py-2.5 font-display text-xs font-bold uppercase tracking-wider text-white transition-colors hover:bg-ember-hover sm:flex"
            >
              Get Free Growth Review <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setMobileOpen(true)}
              className="text-white lg:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-obsidian/98 backdrop-blur-xl lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 py-6">
              <span className="font-display text-sm font-extrabold tracking-[0.18em]">GROTECH WEB</span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setMobileOpen(false)}
                aria-label="Close menu"
                className="text-white"
              >
                <X className="h-7 w-7" />
              </button>
            </div>
            <nav className="flex flex-col gap-1 px-6 pt-6">
              {[
                { label: "Services", to: "/services/" },
                { label: "Pricing", to: "/pricing/" },
                { label: "Work", to: "/projects/" },
                { label: "Insights", to: "/blog/" },
                { label: "Reviews", to: "/reviews/" },
                { label: "About", to: "/about/" },
                { label: "Contact", to: "/contact/" },
              ].map((l, i) => (
                <motion.div
                  key={l.label}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i }}
                >
                  <Link
                    to={l.to}
                    onClick={() => setMobileOpen(false)}
                    data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                    className="block border-b border-white/10 py-4 text-left font-display text-3xl font-bold text-white"
                  >
                    {l.label}
                  </Link>
                </motion.div>
              ))}
              <p className="pt-6 font-mono text-xs uppercase tracking-[0.3em] text-ember">Services</p>
              <div className="grid grid-cols-1 gap-1 pt-2">
                {SERVICES.map((s) => (
                  <Link
                    key={s.slug}
                    to={`/${s.slug}/`}
                    onClick={() => setMobileOpen(false)}
                    className="py-2 text-sm text-white/60"
                  >
                    {s.title}
                  </Link>
                ))}
              </div>
              <button
                data-testid="mobile-nav-cta"
                onClick={() => { setMobileOpen(false); openLeadPopup(); }}
                className="mt-6 flex items-center justify-center gap-2 rounded-full bg-ember py-4 font-display text-sm font-bold uppercase tracking-wider text-white"
              >
                Get Free Growth Review <ArrowUpRight className="h-4 w-4" />
              </button>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
